import { useState, useEffect } from "react";
import { Wallet, ArrowDown, ArrowUp, TrendingDown, Building, Info, Clock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function MetricRow({ label, value, type = "neutral", tooltip }) {
  const color = type === "add" ? "text-emerald-400" : type === "subtract" ? "text-red-400" : "text-white/80";
  const prefix = type === "add" ? "+ " : type === "subtract" ? "- " : "";
  return (
    <div className="flex items-center justify-between py-3 border-b border-white/[0.04] last:border-0">
      <div className="flex items-center gap-2">
        <span className="text-sm text-white/60">{label}</span>
        {tooltip && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger>
                <Info className="w-3 h-3 text-white/20" />
              </TooltipTrigger>
              <TooltipContent className="bg-[#000000] border border-white/10 text-white/70 text-xs max-w-xs">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <span className={`text-sm font-semibold ${color}`}>
        {prefix}${Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </span>
    </div>
  );
}

export default function CashTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API}/quickbooks/cash`, { headers });
        if (!res.ok) return;
        setData(await res.json());
      } catch {
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <Clock className="w-10 h-10 text-white/20 mb-4" />
        <p className="text-white/40 text-sm">No data yet. Run a sync first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cash-tab">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Total Cash</p>
          <p className="text-3xl font-light text-emerald-400 tracking-tight">
            ${data.total_cash.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">7-Day Outflows</p>
          <p className="text-2xl font-light text-red-400 tracking-tight">
            ${data.outflow_7_days.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">30-Day Inflows</p>
          <p className="text-2xl font-light text-blue-400 tracking-tight">
            ${data.inflow_30_days.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Operating Runway</p>
          <p className={`text-2xl font-light tracking-tight ${data.runway_months > 3 ? 'text-emerald-400' : data.runway_months > 1 ? 'text-amber-400' : 'text-red-400'}`}>
            {data.runway_months > 12 ? "12+" : data.runway_months} months
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bank Accounts */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-sm font-medium text-white/60 mb-4 uppercase tracking-wider flex items-center gap-2">
            <Building className="w-4 h-4" />
            Bank Accounts
          </h3>
          {data.bank_accounts.length === 0 ? (
            <p className="text-white/30 text-sm py-6 text-center">No bank accounts found</p>
          ) : (
            <div className="space-y-0">
              {data.bank_accounts.map((acc, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-white/[0.04] last:border-0">
                  <div>
                    <p className="text-sm text-white/80 font-medium">{acc.name}</p>
                    <p className="text-[10px] text-white/30 mt-0.5">{acc.account_type}</p>
                  </div>
                  <p className={`text-sm font-semibold ${acc.balance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    ${acc.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </p>
                </div>
              ))}
              <div className="flex items-center justify-between pt-3 mt-1">
                <p className="text-xs text-white/50 font-medium uppercase tracking-wider">Consolidated</p>
                <p className="text-base font-bold text-white">
                  ${data.total_cash.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Cash Waterfall */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-sm font-medium text-white/60 mb-4 uppercase tracking-wider flex items-center gap-2">
            <TrendingDown className="w-4 h-4" />
            Cash Waterfall — Month End Projection
          </h3>
          <div>
            <MetricRow
              label="Current Cash"
              value={data.waterfall.current_cash}
              tooltip="Sum of all active bank account balances"
            />
            <MetricRow
              label="Scheduled A/P"
              value={data.waterfall.less_scheduled_ap}
              type="subtract"
              tooltip="Total open bill balances (known payables)"
            />
            <MetricRow
              label="Expected Receivables"
              value={data.waterfall.plus_expected_ar}
              type="add"
              tooltip="Invoice balances due by end of this month"
            />
            <div className="flex items-center justify-between pt-4 mt-2 border-t-2 border-white/[0.08]">
              <span className="text-sm font-medium text-white/70">Projected Month-End Cash</span>
              <span className={`text-xl font-light ${data.waterfall.projected_month_end >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                ${data.waterfall.projected_month_end.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
